import { Button } from 'reactstrap';
import { useContext, useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { CampaignContext } from '../../context/campaign';
import {
    fetchMyCampaign,
    fetchUpdates,
    postCampaignUpdate,
    putCampaignUpdate,
    deleteCampaignUpdate,
} from '../../services/campaign';
import {
    CampaignUpdateFormModal,
    ICampaignUpdate,
    MyCampaignResponse,
} from '../../types/campaign';
import TableContainer from './components/TableContainer';
import { CampaignHeaderSection } from '../../shared/components/cards/CampaignItemsCard';
import { renderIcon } from '../../shared/components/icons';
import { Loading } from '../../shared/components/Loading';
import { ConfirmModal } from '~/shared/components/ConfirmModal';
import { CampaignUpdateModal } from './components/CampaignUpdateModal';
import toast from 'react-hot-toast';
import moment from 'moment';
import { ColumnDef } from '@tanstack/react-table';
import { NoDataCard } from './components/NoDataCard';
import { AdminContext } from '~/context/admin';

const TITLE = `You don't have any campaign updates yet`;
const BODY = `This is where you can post any news about the progress of your health or the campaign. Add your first post here.`;

export const CampaignUpdates = ({}: any) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const [updates, setUpdates] = useState<ICampaignUpdate[]>([]);
    const [updateModal, setUpdateModal] = useState<boolean>(false);
    const [editUpdate, setEditUpdate] = useState<ICampaignUpdate | null>(null);
    const [deleteUpdate, setDeleteUpdate] = useState<ICampaignUpdate | null>(
        null
    );

    const history = useHistory();
    function UpdatesText() {
        const enablePublishFeature = import.meta.env.VITE_ENABLE_PUBLISH_CAMPAIGN === 'true';
        // Ensure wordpress_permalink is a valid URL (starts with http), not just truthy
        // This prevents showing the button when permalink is null, empty, or the string "null"
        const hasValidPermalink = campaign?.campaignDetail?.wordpress_permalink?.startsWith('http') ?? false;
        const isPublished = !!(campaign?.campaignDetail?.wordpress_id && hasValidPermalink);
        const showViewButton = enablePublishFeature 
            ? isPublished 
            : ['disbursable', 'funded'].includes(campaign?.campaignDetail?.campaign_status_code);
        
        return (
            <div>
                <h1 className='h2'>Updates</h1>
                <p className='d-flex gap-2'>
                    <Button
                        color='primary'
                        size='sm'
                        onClick={() => {
                            setEditUpdate(null);
                            setUpdateModal(true);
                        }}
                    >
                        {renderIcon('add')}
                        Post Campaign Update
                    </Button>
                    {showViewButton && (
                        <Button
                            size='sm'
                            onClick={() => {
                                if (enablePublishFeature && hasValidPermalink) {
                                    // Use the validated wordpress_permalink
                                    window.open(campaign?.campaignDetail?.wordpress_permalink, '_blank');
                                } else if (!enablePublishFeature) {
                                    // Legacy URL pattern using campaign_id
                                    window.open(
                                        `${import.meta.env.VITE_WP_URL}/campaign/${campaign?.campaignDetail?.campaign_id}`,
                                        '_blank'
                                    );
                                }
                            }}
                            style={{
                                backgroundColor: '#01aeb4',
                                borderColor: '#01aeb4',
                                color: '#fff'
                            }}
                        >
                            View Campaign
                        </Button>
                    )}
                </p>
            </div>
        );
    }

    const data = useMemo(() => {
        if (updates) {
            return updates.map((update) => {
                const { id, created_date, image_url, video_url } = update;
                return {
                    id,
                    created_date: moment(created_date).format('MM/D/Y hh:mm A'),
                    body: update.body,
                    image_url,
                    video_url,
                };
            });
        } else {
            return [];
        }
    }, [updates]);

    const columns = useMemo<ColumnDef<ICampaignUpdate>[]>(
        () => [
            {
                id: 'date',
                header: 'Date',
                accessorKey: 'created_date',
            },
            {
                id: 'update',
                header: 'Update',
                accessorKey: 'body',
                accessorFn: (row, i) => {
                    return (
                        <div
                            style={{ whiteSpace: 'pre-wrap' }}
                            dangerouslySetInnerHTML={{ __html: row.body }}
                        ></div>
                    );
                },
            },
            {
                id: 'actions',
                header: '',
                accessorKey: '',
                accessorFn: (props: ICampaignUpdate) => {
                    return (
                        <span className='d-flex justify-content-end'>
                            <Button
                                onClick={() => {
                                    const thisUpdate = updates.find(
                                        (u) => u.id === props.id
                                    );
                                    if (thisUpdate) setEditUpdate(thisUpdate);
                                }}
                                color='link'
                                className='p-1'
                            >
                                {renderIcon('edit')}
                            </Button>
                            <Button
                                onClick={() => {
                                    const thisUpdate = updates.find(
                                        (u) => u.id === props.id
                                    );
                                    if (thisUpdate) setDeleteUpdate(thisUpdate);
                                }}
                                color='link'
                                className='p-1 text-danger'
                            >
                                {renderIcon('delete')}
                            </Button>
                        </span>
                    );
                },
            },
        ],
        [updates]
    );

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        }
    }, []);

    useEffect(() => {
        if (campaign) {
            if (campaign.detail != 'Not found.') {
                if (!updates.length && !loading) {
                    getUpdates();
                }
            }
        }
    }, [campaign]);

    useEffect(() => {
        if (editUpdate) {
            setUpdateModal(true);
        }
    }, [editUpdate]);

    const getUpdates = async () => {
        if (campaign) {
            try {
                setLoading(true);
                const resp: ICampaignUpdate[] | { detail: string } =
                    await fetchUpdates(campaign.campaignDetail.campaign_id);

                if ('detail' in resp) {
                    if (resp.detail === 'Invalid token.') {
                        // Delete localStorage token
                        localStorage.removeItem('token');
                        history.push('/login');
                    } else {
                        toast.error(resp.detail);
                    }
                } else {
                    setUpdates(resp);
                }

                setLoading(false);
            } catch (e) {
                console.log('getUpdates() e: ', e);
            }
        }
    };

    const getMyCampaign = async () => {
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail && resp.detail === 'Invalid token.') {
                // Delete localStorage token
                localStorage.removeItem('token');
                history.push('/login');
            }
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
            setLoading(false);
        } catch (e) {
            console.log('getMyCampaign() e: ', e);
        }
    };

    if (!campaign) {
        return (
            <div>
                <Loading />
            </div>
        );
    }

    const handleSuccess = () => {
        setUpdateModal(false);
        const { wordpress_permalink } = campaignDetail;
        if (wordpress_permalink) {
            toast.success(
                <div>
                    View your{' '}
                    <a
                        href={`${import.meta.env.VITE_WP_URL}/campaign/${
                            campaignDetail?.campaign_id
                        }`}
                        target='_blank'
                    >
                        Campaign Update here!
                    </a>
                </div>
            );
        } else {
            toast.success('Campaign Update posted!');
        }
    };

    const { campaignDetail }: MyCampaignResponse = campaign;

    const onSubmitUpdate = async (payload: CampaignUpdateFormModal) => {
        try {
            setSaving(true);
            let resp;
            if (editUpdate) {
                resp = await putCampaignUpdate(
                    payload,
                    editUpdate.id,
                    campaignId
                );
                if ('detail' in resp) {
                    if (resp.detail === 'Invalid token.') {
                        // Delete localStorage token
                        localStorage.removeItem('token');
                        history.push('/login');
                    } else {
                        toast.error(resp.detail);
                    }
                } else {
                    const updateIndex = updates?.findIndex(
                        (x) => x.id === editUpdate.id
                    );
                    let newUpdates = [...updates];
                    newUpdates[updateIndex] = Object.assign(
                        {},
                        updates[updateIndex],
                        resp
                    );
                    setUpdates(newUpdates);
                    handleSuccess();
                }
            } else {
                resp = await postCampaignUpdate(payload, campaignId);
                if ('detail' in resp) {
                    if (resp.detail === 'Invalid token.') {
                        // Delete localStorage token
                        localStorage.removeItem('token');
                        history.push('/login');
                    } else {
                        toast.error(resp.detail);
                    }
                } else {
                    setUpdates([resp, ...updates]);
                    handleSuccess();
                }
            }

            setSaving(false);
        } catch (e) {
            console.log('e: ', e);
            setSaving(false);
        }
    };

    const onDeleteUpdate = async () => {
        try {
            if (!deleteUpdate || !deleteUpdate.id) {
                return;
            }
            setSaving(true);
            const resp = await deleteCampaignUpdate(
                deleteUpdate.id,
                campaignId
            );
            const newUpdates = updates.filter((x) => x.id !== deleteUpdate.id);
            setUpdates(newUpdates);
            setSaving(false);
            setDeleteUpdate(null);
            toast.success('Campaign update deleted!');
        } catch (e) {
            console.log('e: ', e);
            setSaving(false);
        }
    };

    return (
        <div className='container__inner'>
            <CampaignHeaderSection leftcard={<UpdatesText />} />
            <div className='pb-5'>
                <TableContainer
                    columns={columns}
                    data={data}
                    showPaginationComponents={true}
                    showHeaders
                    NoDataCard={<NoDataCard title={TITLE} body={BODY} />}
                />
            </div>

            <CampaignUpdateModal
                key={editUpdate?.id || 'new'}
                isOpen={updateModal}
                toggle={() => {
                    setUpdateModal(false);
                    setEditUpdate(null);
                }}
                onSubmit={onSubmitUpdate}
                saving={saving}
                campaignId={campaignDetail?.campaign_id}
                update={editUpdate}
            />
            <ConfirmModal
                isOpen={!!deleteUpdate}
                title='Delete Update?'
                message='Are you sure you wish to delete this update?'
                toggle={() => setDeleteUpdate(null)}
                handleConfirm={onDeleteUpdate}
                saving={saving}
                isDeleting={true}
            />
        </div>
    );
};
